<template>
  <!--
    Tab "Elementos": tabla de solo lectura de los elementos de la capa con sus
    atributos extra. Server-side: paginación + filtros (miles de elementos).
    Fetch lazy (al activar el tab, vía refresh() desde el manager).
    Arriba: descarga del archivo importado (placeholder hasta vincular el Excel).
  -->
  <div class="flex flex-col w-full">

    <!-- Barra superior: descarga + filtros -->
    <div class="ea-toolbar">
      <!-- Descarga del archivo importado + historial de versiones -->
      <b-dropdown v-if="document" aria-role="list" position="is-bottom-right" :max-height="280" scrollable>
        <template #trigger>
          <b-button type="is-light" size="is-small" icon-pack="fas" icon-left="download" icon-right="angle-down">
            Descargar archivo importado
          </b-button>
        </template>
        <b-dropdown-item aria-role="listitem" @click="downloadCurrent">
          <div class="flex items-center gap-2">
            <b-icon pack="fas" icon="file-excel" size="is-small" style="color:#16a34a;" />
            <span>{{ document.original_filename }} <span class="text-xs" style="color:#7D99D4;">(actual)</span></span>
          </div>
        </b-dropdown-item>
        <template v-if="versions.length">
          <hr class="dropdown-divider" />
          <div class="px-3 py-1 text-xs" style="color:#7D99D4;">Versiones anteriores</div>
          <b-dropdown-item v-for="v in versions" :key="v.id" custom aria-role="listitem">
            <span class="text-xs" style="color:#385DA5;">{{ v.original_filename }}</span>
            <span class="text-xs ml-1" style="color:#9ca3af;">· {{ v.created_at }}</span>
          </b-dropdown-item>
        </template>
      </b-dropdown>
      <b-tooltip v-else label="Aún no se ha importado un archivo en esta capa" position="is-bottom">
        <b-button type="is-light" size="is-small" icon-pack="fas" icon-left="download" disabled>
          Descargar archivo importado
        </b-button>
      </b-tooltip>

      <div class="flex-1"></div>

      <!-- Filtros -->
      <b-field class="mb-0">
        <b-input
          v-model="search"
          placeholder="Buscar por ID, pedido o línea…"
          icon-pack="fas"
          icon="magnifying-glass"
          size="is-small"
          @input="onSearchInput"
          clearable
          style="min-width: 240px;"
        />
      </b-field>
    </div>

    <!-- Tabla server-side (sin card contenedor) -->
      <b-table
        class="ea-table"
        :data="items"
        :loading="isLoading"
        paginated
        backend-pagination
        :total="total"
        :per-page="perPage"
        :current-page="page"
        @page-change="onPageChange"
        :narrowed="true"
        icon-pack="fas"
      >
        <b-table-column field="code" label="ID" v-slot="props">
          <span class="font-medium" style="color:#385DA5;">{{ props.row.code || '—' }}</span>
        </b-table-column>
        <b-table-column field="order_line_id" label="Línea de pedido" v-slot="props">
          {{ props.row.order_line_id || '—' }}
        </b-table-column>
        <b-table-column field="purchase_order_id" label="Pedido" v-slot="props">
          {{ props.row.purchase_order_id || '—' }}
        </b-table-column>
        <b-table-column v-if="isPilings" field="motor_type" label="Motor / No motor / Ext." v-slot="props">
          {{ props.row.motor_type || '—' }}
        </b-table-column>
        <b-table-column v-if="isPilings" field="foundation_type" label="Cimentación" v-slot="props">
          {{ props.row.foundation_type || '—' }}
        </b-table-column>
        <b-table-column v-if="isPilings" field="embedment_depth" label="Empotramiento" numeric v-slot="props">
          {{ props.row.embedment_depth != null ? props.row.embedment_depth : '—' }}
        </b-table-column>
        <b-table-column v-if="isPilings" field="section" label="Sección" v-slot="props">
          {{ props.row.section || '—' }}
        </b-table-column>

        <template #empty>
          <div class="has-text-centered py-6 text-sm" style="color:#7D99D4;">
            {{ isLoading ? 'Cargando…' : ('No hay ' + elementNoun + ' que coincidan con el filtro.') }}
          </div>
        </template>

        <template slot="bottom-left">
          <span class="text-xs" style="color: rgb(51, 81, 143);">{{ total.toLocaleString() }} {{ elementNoun }}</span>
        </template>
      </b-table>
  </div>
</template>

<script>
import { getExtraAttributesList, getExtraAttributesDocument } from '../../services/layers.service';

export default {
  name: 'LayerExtraAttributesTable',

  props: {
    layer: { type: Object, required: true },
  },

  data() {
    return {
      items: [],
      total: 0,
      page: 1,
      perPage: 25,
      search: '',
      isLoading: false,
      _searchTimer: null,
      _loaded: false,   // fetch inicial diferido (lazy)
      document: null,   // documento (Excel) actual del import
      versions: [],     // historial de versiones
    };
  },

  computed: {
    // Sustantivo del tipo de elemento según la capa (ej. "hincas" para pilings).
    elementNoun() {
      const map = {
        pilings: 'hincas',
        structures: 'estructuras',
        modules: 'módulos',
      };
      return map[this.layer?.code] || 'elementos';
    },
    // Las columnas motor/cimentación/empotramiento/sección son atributos de
    // pilings; para structures/modules no aplican y se ocultan.
    isPilings() {
      return this.layer?.code === 'pilings';
    },
  },

  methods: {
    async fetchList() {
      if (!this.layer?.id) return;
      this.isLoading = true;
      try {
        // Siempre solo elementos CON asignación (order_line_id) — listar los que
        // no tienen no aporta.
        const params = { page: this.page, per_page: this.perPage, with_data: 1 };
        if (this.search) params.search = this.search;

        const res = await getExtraAttributesList(this.layer.id, params);
        this.items = Array.isArray(res.items) ? res.items : [];
        this.total = res.total || 0;
        this._loaded = true;
      } catch (e) {
        this.$toast?.open?.({
          message: 'No se pudieron cargar los elementos de la capa.',
          type: 'is-danger',
          position: 'is-top-right',
        });
      } finally {
        this.isLoading = false;
      }
    },

    onPageChange(page) {
      this.page = page;
      this.fetchList();
    },

    // Debounce del search para no golpear el backend en cada tecla.
    onSearchInput() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.page = 1;
        this.fetchList();
      }, 350);
    },

    async fetchDocument() {
      if (!this.layer?.id) return;
      try {
        const res = await getExtraAttributesDocument(this.layer.id);
        this.document = res.document || null;
        this.versions = Array.isArray(res.versions) ? res.versions : [];
      } catch (e) {
        this.document = null;
        this.versions = [];
      }
    },

    downloadCurrent() {
      if (this.document?.url) {
        window.open(this.document.url, '_blank', 'noopener');
      }
    },

    // Refresco público — el manager lo llama al activar el tab (lazy + post-import).
    refresh() {
      this.fetchList();
      this.fetchDocument();
    },
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
  },
};
</script>

<style scoped>
.ea-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 4px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

/* Tabla: SOLO el header lleva color; el cuerpo queda neutro */
.ea-table >>> th {
  background-color: rgb(232, 241, 251) !important;
  color: rgb(51, 81, 143) !important;
  font-weight: 700;
}
</style>
